import { useState, useEffect, useCallback, RefObject } from 'react';

/**
 * State representing the current text selection
 */
export interface TextHighlightState {
  /** The selected text content */
  selectedText: string;
  /** Position for the popup in viewport coordinates (for fixed positioning) */
  position: { top: number; left: number } | null;
  /** Whether a valid selection exists */
  isVisible: boolean;
}

/**
 * Result returned by the useTextHighlightLearnMore hook
 */
export interface UseTextHighlightResult extends TextHighlightState {
  /** Clear the current selection and hide the popup */
  clearSelection: () => void;
  /** Handle the "Learn More" action with the current selection */
  handleLearnMore: () => void;
}

/**
 * React hook for detecting text selection within a container and providing
 * a "Learn More" action that creates a query from the selected text.
 *
 * When text is selected within the container, the hook tracks the selection
 * and its position. The handleLearnMore callback can be used to submit a
 * query about the selected text.
 *
 * @example
 * ```tsx
 * function ChatComponent() {
 *   const messagesRef = useRef<HTMLDivElement>(null);
 *
 *   const handleSubmitQuery = (query: string) => {
 *     setInputValue(query);
 *     handleSubmit();
 *   };
 *
 *   const highlight = useTextHighlightLearnMore(messagesRef, handleSubmitQuery);
 *
 *   return (
 *     <div ref={messagesRef} className="relative">
 *       {messages.map(msg => <Message key={msg.id} {...msg} />)}
 *       {highlight.isVisible && highlight.position && (
 *         <LearnMorePopup
 *           position={highlight.position}
 *           onClick={highlight.handleLearnMore}
 *         />
 *       )}
 *     </div>
 *   );
 * }
 * ```
 */
export function useTextHighlightLearnMore(
  containerRef: RefObject<HTMLElement | null>,
  onLearnMore: (query: string) => void,
  disabled: boolean = false
): UseTextHighlightResult {
  const [state, setState] = useState<TextHighlightState>({
    selectedText: '',
    position: null,
    isVisible: false,
  });

  const clearSelection = useCallback(() => {
    setState({
      selectedText: '',
      position: null,
      isVisible: false,
    });
    // Clear the browser's selection
    window.getSelection()?.removeAllRanges();
  }, []);

  const handleLearnMore = useCallback(() => {
    if (!state.selectedText) return;

    const query = `Tell me more about: ${state.selectedText}`;
    onLearnMore(query);
    clearSelection();
  }, [state.selectedText, onLearnMore, clearSelection]);

  useEffect(() => {
    if (disabled) {
      // Clear any existing selection when disabled
      if (state.isVisible) {
        setState({
          selectedText: '',
          position: null,
          isVisible: false,
        });
      }
      return;
    }

    // Only check selection on mouseup to avoid interfering with drag-to-select
    const handleMouseUp = () => {
      // Small delay to let the selection finalize
      setTimeout(() => {
        const selection = window.getSelection();

        // No selection or collapsed (just a cursor)
        if (!selection || selection.isCollapsed) {
          setState({
            selectedText: '',
            position: null,
            isVisible: false,
          });
          return;
        }

        // Check if selection is within our container
        if (!containerRef.current) return;

        const range = selection.getRangeAt(0);
        const ancestor = range.commonAncestorContainer;

        // The ancestor might be a text node, so check its parent too
        const ancestorElement =
          ancestor.nodeType === Node.TEXT_NODE ? ancestor.parentElement : ancestor;

        if (!containerRef.current.contains(ancestorElement)) {
          setState({
            selectedText: '',
            position: null,
            isVisible: false,
          });
          return;
        }

        const selectedText = selection.toString().trim();

        // Ignore empty or very short selections
        if (selectedText.length < 2) {
          setState({
            selectedText: '',
            position: null,
            isVisible: false,
          });
          return;
        }

        // Get position from selection bounding rect (viewport coordinates for fixed positioning)
        const rect = range.getBoundingClientRect();

        // Position the popup below the selection, centered horizontally
        setState({
          selectedText,
          position: {
            top: rect.bottom + 8,
            left: rect.left + rect.width / 2,
          },
          isVisible: true,
        });
      }, 10);
    };

    // Clear selection when clicking elsewhere (not on popup)
    const handleMouseDown = (e: MouseEvent) => {
      // Don't clear if clicking on the popup
      const target = e.target as HTMLElement;
      if (target.closest('[data-learn-more-popup]')) return;

      if (state.isVisible) {
        setState({
          selectedText: '',
          position: null,
          isVisible: false,
        });
      }
    };

    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [containerRef, disabled, state.isVisible]);

  return {
    ...state,
    clearSelection,
    handleLearnMore,
  };
}
